// ---- Painted UI icon URLs (THE path registry) ----
// Bitmaps: assets/ui/icons/ → GET /ui/icons/:file (index.ts).
// Style: knowledge-base/reference/ui-icon-art-style.md + skill ui-icon-authoring.
// Naming: health|stamina · skill-<id> · branch-<id> — see that doc's ownership table
// (hunting = no bow; bows = combat ranged only).
//
// LAZY LOADING: skill + branch crests are NOT fetched at boot. Journal calls
// `warmSkillIcons()` on first open. Vitals (health/stamina) stay eager on the HUD.
// Do not scatter "/ui/icons/..." strings outside this file.

/** Always-on vitals icons (HUD bottom-right). */
export const UI_ICON = {
  health: "/ui/icons/health.png",
  stamina: "/ui/icons/stamina.png",
} as const;

/**
 * Skill crests keyed by skill id (`content/skills/<id>.json`).
 * Only assign to `<img>.src` after Journal open (or via warmSkillIcons).
 */
export const UI_SKILL_ICON: Readonly<Record<string, string>> = {
  combat: "/ui/icons/skill-combat.png",
  fishing: "/ui/icons/skill-fishing.png",
  hunting: "/ui/icons/skill-hunting.png",
  cooking: "/ui/icons/skill-cooking.png",
};

/**
 * Branch tab crests keyed by branch id (melee, ranged, angling, …).
 * Hunting uses stag/tracks/trap — not bow (bow is combat ranged).
 */
export const UI_BRANCH_ICON: Readonly<Record<string, string>> = {
  melee: "/ui/icons/branch-melee.png",
  ranged: "/ui/icons/branch-ranged.png",
  angling: "/ui/icons/branch-angling.png",
  netting: "/ui/icons/branch-netting.png",
  stalking: "/ui/icons/branch-stalking.png",
  trapping: "/ui/icons/branch-trapping.png",
  cooking: "/ui/icons/branch-cooking.png",
  brewing: "/ui/icons/branch-brewing.png",
};

/** Emoji fallback when a skill has no painted crest yet. */
export const SKILL_CREST_EMOJI: Readonly<Record<string, string>> = {
  combat: "⚔",
  fishing: "🎣",
  hunting: "🦌",
  cooking: "🍲",
};

/** Emoji fallback for branch tabs. */
export const BRANCH_CREST_EMOJI: Readonly<Record<string, string>> = {
  melee: "⚔",
  ranged: "🏹",
  angling: "🎣",
  netting: "🪢",
  stalking: "🦌",
  trapping: "⬡",
  cooking: "🍳",
  brewing: "⚗",
};

let skillIconsWarmed = false;

/**
 * Prefetch skill + branch crests once (first Journal open). Safe to call every
 * open — only the first call starts downloads. Does not block.
 */
export function warmSkillIcons(): void {
  if (skillIconsWarmed || typeof Image === "undefined") return;
  skillIconsWarmed = true;
  for (const url of [
    ...Object.values(UI_SKILL_ICON),
    ...Object.values(UI_BRANCH_ICON),
  ]) {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
  }
}

function makePaintedImg(url: string, cls: string): HTMLImageElement {
  const img = document.createElement("img");
  img.className = `${cls} painted`;
  img.alt = "";
  img.draggable = false;
  img.decoding = "async";
  img.loading = "lazy";
  // src only when called (Journal render / after warm) — never at module import.
  img.src = url;
  img.setAttribute("aria-hidden", "true");
  return img;
}

/**
 * Build a skill crest for Journal cards / headers.
 * Painted plate when authored; else emoji shell.
 */
export function makeSkillCrestEl(skillId: string, cls = "skill-crest"): HTMLElement {
  const url = UI_SKILL_ICON[skillId];
  if (url) return makePaintedImg(url, cls);
  const div = document.createElement("div");
  div.className = cls;
  div.textContent = SKILL_CREST_EMOJI[skillId] ?? "✦";
  div.setAttribute("aria-hidden", "true");
  return div;
}

/**
 * Build a branch tab crest (Melee / Ranged / Angling / …).
 * Painted plate when authored; else emoji.
 */
export function makeBranchCrestEl(branchKey: string, cls = "bt-crest"): HTMLElement {
  const url = UI_BRANCH_ICON[branchKey];
  if (url) return makePaintedImg(url, cls);
  const span = document.createElement("span");
  span.className = cls;
  span.textContent = BRANCH_CREST_EMOJI[branchKey] ?? "✦";
  span.setAttribute("aria-hidden", "true");
  return span;
}
